import clsx from "clsx";
import React from "react";

import { IClassScores, IReferenceScores } from "../../../state";

import styles from "./AssessmentListSummaryScore.module.scss";

type Props = {
  referenceScores: {
    classScores: IClassScores | null;
    gradeScores: IReferenceScores | null;
    nationalScores: IReferenceScores | null;
  };
  selectedExperienceNo: number;
};

const getSelectedItem = (
  scores: IClassScores | IReferenceScores | null,
  selectedExperienceNo: number,
) => {
  return selectedExperienceNo === 0
    ? scores
    : scores?.assessment_experiences?.find(
        a => a.experience_no === selectedExperienceNo,
      );
};

const AssessmentListSummaryScore = ({
  referenceScores,
  selectedExperienceNo,
}: Props) => {
  const { classScores, gradeScores, nationalScores } = referenceScores;
  const currentScore = React.useMemo(() => {
    const value = getSelectedItem(classScores, selectedExperienceNo)?.avg_score;
    return value ?? null;
  }, [classScores, selectedExperienceNo]);

  const averageScoreText = React.useMemo(() => {
    return currentScore === null ? "" : currentScore.toFixed(1);
  }, [currentScore]);

  const difference = React.useMemo(() => {
    const item = getSelectedItem(classScores, selectedExperienceNo);
    return (item as IClassScores)?.avg_difference ?? null;
  }, [classScores, selectedExperienceNo]);

  const gradeScoreText = React.useMemo(() => {
    const value = getSelectedItem(gradeScores, selectedExperienceNo)?.avg_score;
    return value?.toFixed(1) ?? "";
  }, [gradeScores, selectedExperienceNo]);

  const nationalScoreText = React.useMemo(() => {
    const value = getSelectedItem(nationalScores, selectedExperienceNo)
      ?.avg_score;
    return value?.toFixed(1) ?? "";
  }, [nationalScores, selectedExperienceNo]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        このクラスの
        <br />
        平均スコア
      </div>
      <div
        className={clsx(
          styles.scoreArea,
          difference !== null && difference > 0 && styles.high,
          difference !== null && difference < 0 && styles.low,
        )}
      >
        <div className={styles.average}>
          {!averageScoreText ? (
            <span className={styles.score}>&nbsp;-</span>
          ) : (
            <span className={styles.score}>
              {averageScoreText.substring(0, 1)}
              <span className={styles.sub}>
                {averageScoreText.substring(1)}
              </span>
            </span>
          )}
        </div>
        <div className={styles.difference}>
          <span className={styles.title}>前回差</span>
          {difference === null ? (
            <span className={styles.score}>-&nbsp;</span>
          ) : (
            <span className={styles.score}>{`${
              difference > 0 ? "+" : ""
            }${difference.toFixed(1)}`}</span>
          )}
        </div>
      </div>
      <div className={styles.referenceScoreArea}>
        <div className={styles.referenceScore}>
          <span className={styles.title}>自校の学年平均</span>
          {!gradeScoreText ? (
            <span className={styles.noData}>&nbsp;-</span>
          ) : (
            <span className={styles.score}>{gradeScoreText}</span>
          )}
        </div>
        <div className={styles.referenceScore}>
          <span className={styles.title}>全国平均</span>
          {!nationalScoreText ? (
            <span className={styles.noData}>相場データなし</span>
          ) : (
            <span className={styles.score}>{nationalScoreText}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentListSummaryScore;
